<template>
  <div class="flex h-screen overflow-hidden">
    <Sidebar :role="role" :collapsed="collapsed" :mobileOpen="mobileSidebarOpen" :isDesktop="isDesktop"
      @toggle="toggleSidebar" @close-mobile="closeMobileSidebar" />
    <div class="flex flex-column flex-1 min-w-0 transition-all duration-300 ease-in-out"
      :class="[isDesktop ? (collapsed ? 'md:ml-6rem' : 'md:ml-16rem') : '']">
      <Topbar :role="role" :image="(profileImageUrl && profileImageUrl !== 'Service is not available in local environment')
        ? profileImageUrl : blankProfile" @toggle-sidebar="toggleMobileSidebar" @open-chat="openChatList" />
      <main class="flex-1 content-area overflow-y-auto transition-all duration-300 z-20">
        <div class="container-custom mx-auto pt-0">
          <slot />
        </div>
      </main>
    </div>

    <Teleport to="body">
      <ChatManager v-if="isChatListOpen" @close="isChatListOpen = false" @select-channel="openSelectedChat" />
      <FloatingChatWindow :activeChannel="activeChannel" @close="activeChannel = null" />
    </Teleport>
  </div>
</template>

<script setup>
import { ref, shallowRef, onMounted, onUnmounted, defineProps, computed, watch } from "vue";
import Sidebar from "@/components/dashboard/SideBar.vue";
import Topbar from "@/components/dashboard/TopBar.vue";
import blankProfile from '@/assets/blank-profile.png';
import { useAuthStore } from "@/stores/authStore";
import FloatingChatWindow from "@/components/FloatingChatWindow.vue";
import ChatManager from "@/components/ChatManager.vue";

defineProps({
  role: String
});

const authStore = useAuthStore();
const role = computed(() => authStore.user?.role || "");
const collapsed = ref(false);
const mobileSidebarOpen = ref(false);
const isDesktop = ref(window.innerWidth >= 1024);
const profileImageUrl = computed(() => authStore.profileImageUrl);

const isChatListOpen = ref(false);
const activeChannel = shallowRef(null);

// The Lumi chat widget (third-party bundle mounted into #root in public/index.html)
const isChatPanelOpen = computed(() => isChatListOpen.value || !!activeChannel.value);

watch(isChatPanelOpen, (open) => {
  document.body.classList.toggle("app-chat-open", open);
}, { immediate: true });

watch(() => ({
  studentPhoto: authStore.user?.profilePhotoURL,
  professionalPhoto: authStore.user?.professionalPhotoURL
}), () => {
  authStore.loadProfileImage();
}, {
  immediate: true
});
onMounted(() => {
  if (authStore.user && !authStore.profileImageUrl) {
    authStore.loadProfileImage();
  }
  window.addEventListener("resize", handleResize);
});
onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  document.body.classList.remove("app-chat-open");
});

const toggleSidebar = () => {
  if (isDesktop.value)
    collapsed.value = !collapsed.value;
};

const toggleMobileSidebar = () => {
  if (!isDesktop.value)
    mobileSidebarOpen.value = !mobileSidebarOpen.value;
};

const closeMobileSidebar = () => {
  mobileSidebarOpen.value = false;
};

const handleResize = () => {
  isDesktop.value = window.innerWidth >= 1024;
  if (isDesktop.value)
    mobileSidebarOpen.value = false;
};

const openSelectedChat = (channel) => {
  activeChannel.value = channel;
  isChatListOpen.value = false;
};

const openChatList = () => {
  isChatListOpen.value = true;
};

// eslint-disable-next-line no-undef
defineExpose({
  openChatList,
  openSelectedChat
});
</script>

<!-- Global: #root (the Lumi chat widget) lives outside the app root -->
<style>
@media (max-width: 600px), (max-height: 780px) {
  body.app-chat-open #root {
    display: none !important;
  }
}
</style>

<style scoped>
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

.content-area {
  margin-top: -29.5vh !important;
  position: relative !important;
  z-index: 20 !important;
  margin: 18px 18px 0px 18px;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.container-custom {
  display: flex;
  border-radius: 8px;
  padding: 10px;
  background-color: white;
  height: auto;
  width: 100%;
  display: inline-block;
  min-height: 50vh;

}

@media (max-width: 767px) {
  .content-area {
    margin-top: -1rem;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .container-custom {
    border-radius: 8px;

    display: inline-block;

  }
}

@media (max-width: 600px) {

  .content-area {
    margin-top: -24.5vh !important;
  }
}
</style>
